import type {
  BuyerInterviewOutcomeDto,
  BuyerQualificationDimension,
  QualificationVerdict,
} from "@/api/buyers";

/**
 * Which recorded answers contributed to what, for the READ-ONLY interview view.
 *
 * <p>🔴 <b>Nothing here decides anything.</b> Every question id comes from the
 * server (`questionIds` on a dimension, knockout or coherence flag), which
 * reads it from `BuyerQualificationRuleQuestions` — the table beside the
 * scoring rules. This file only re-indexes that answer by question so a row can
 * find its own flags. A Pass dimension is never flagged, and neither is
 * `incomplete`, even if a payload were to carry ids for one.</p>
 *
 * <p>🔴 <b>Never import this from the live interview.</b> The review step of a
 * conduct session shows answers and nothing else; a flag there is a rating
 * hint reaching the specialist mid-call.</p>
 */

export type InterviewFlagKind = "fail" | "concern" | "knockout" | "coherence";

export interface InterviewFlagNote {
  kind: InterviewFlagKind;
  /** Set for a dimension flag: the category the answer counts against. */
  dimension?: BuyerQualificationDimension;
  /** Set for a knockout or coherence flag: the server's sentence. */
  text?: string;
  /** Stable key (rule id or flag code) for React lists. */
  source: string;
}

/** The verdicts that flag an answer. Pass never does; Unknown is "not enough
 *  recorded", which is a gap rather than a finding. */
function flagKindFor(verdict: QualificationVerdict): InterviewFlagKind | null {
  if (verdict === "Fail") return "fail";
  if (verdict === "Concern") return "concern";
  return null;
}

/** Question id → every flag that answer contributed to, in a stable order:
 *  knockouts, then dimensions, then "does not add up". */
export function flagsByQuestion(
  outcome: BuyerInterviewOutcomeDto | null | undefined,
): Map<string, InterviewFlagNote[]> {
  const out = new Map<string, InterviewFlagNote[]>();
  if (!outcome) return out;

  const add = (ids: string[] | null | undefined, note: InterviewFlagNote) => {
    for (const id of ids ?? []) {
      const list = out.get(id) ?? [];
      list.push(note);
      out.set(id, list);
    }
  };

  for (const k of outcome.knockouts) {
    add(k.questionIds, { kind: "knockout", text: k.text, source: k.code });
  }
  for (const d of outcome.dimensions) {
    const kind = flagKindFor(d.verdict);
    if (!kind) continue;
    add(d.questionIds, { kind, dimension: d.dimension, source: d.ruleId });
  }
  for (const c of outcome.coherenceFlags) {
    add(c.questionIds, { kind: "coherence", text: c.text, source: c.code });
  }
  return out;
}
